<template>
  <v-dialog
    :value="value"
    max-width="760"
    persistent
    scrollable
    content-class="feature-tour-dialog"
    @keydown.esc="close"
  >
    <v-card v-if="slides.length" class="rounded-xl overflow-hidden">
      <div class="d-flex align-center px-5 pt-4 pb-2">
        <v-chip small color="primary" class="font-weight-bold" label>
          <v-icon left small>mdi-gift-outline</v-icon>
          {{ $t('featureTours.title') }}
        </v-chip>
        <span class="ms-3 text-caption grey--text">
          {{ $t('featureTours.stepOf', { step: step + 1, total: slides.length }) }}
        </span>
        <v-spacer></v-spacer>
        <v-btn icon small :aria-label="$t('featureTours.close')" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-0">
        <v-window v-model="step" class="feature-tour-window">
          <v-window-item v-for="(slide, index) in slides" :key="slide.tourId + '-' + index">
            <div class="feature-tour-media">
              <video
                v-if="isVideo(slide.media)"
                :src="mediaUrl(slide.media)"
                autoplay
                muted
                loop
                playsinline
              ></video>
              <img v-else :src="mediaUrl(slide.media)" :alt="$t(slide.titleKey)" />
            </div>
            <div class="px-6 pt-5 pb-2">
              <h2 class="text-h6 font-weight-bold grey--text text--darken-4 mb-2">{{ $t(slide.titleKey) }}</h2>
              <p class="text-body-2 grey--text text--darken-1 mb-0 feature-tour-text">{{ $t(slide.textKey) }}</p>
            </div>
          </v-window-item>
        </v-window>
      </v-card-text>

      <v-card-actions class="px-6 pb-5 pt-3">
        <div class="d-flex align-center">
          <button
            v-for="(slide, index) in slides"
            :key="'dot-' + index"
            type="button"
            class="feature-tour-dot"
            :class="{ active: index === step }"
            :aria-label="$t('featureTours.goToStep', { step: index + 1 })"
            @click="step = index"
          ></button>
        </div>
        <v-btn v-if="!isLast" text small class="text-capitalize grey--text ms-2" @click="close">
          {{ $t('featureTours.skip') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="currentSlide.tryRoute"
          text
          color="primary"
          class="text-capitalize"
          @click="tryIt"
        >{{ $t('featureTours.tryItNow') }}</v-btn>
        <v-btn v-if="step > 0" text class="text-capitalize" @click="step--">
          {{ $t('featureTours.back') }}
        </v-btn>
        <v-btn depressed color="primary" class="text-capitalize px-5" @click="next">
          {{ isLast ? $t('featureTours.gotIt') : $t('featureTours.next') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Slide-through "What's new" popup. The parent decides which tours to show
// (see src/feature-tours/feature-tour-service.js); this component only
// renders them and emits `close` with the tour ids once dismissed.
export default {
  props: {
    value: { type: Boolean, default: false },
    tours: { type: Array, default: () => [] },
  },
  data() {
    return { step: 0 };
  },
  computed: {
    slides() {
      return this.tours.reduce(
        (all, tour) => all.concat(tour.slides.map((slide) => ({ ...slide, tourId: tour.id }))),
        []
      );
    },
    currentSlide() {
      return this.slides[this.step] || {};
    },
    isLast() {
      return this.step >= this.slides.length - 1;
    },
  },
  watch: {
    value(open) {
      if (open) this.step = 0;
    },
  },
  methods: {
    mediaUrl(path) {
      if (/^https?:\/\//.test(path)) return path;
      return process.env.BASE_URL + String(path).replace(/^\//, '');
    },
    isVideo(path) {
      return /\.(mp4|webm)$/i.test(path || '');
    },
    next() {
      if (this.isLast) this.close();
      else this.step++;
    },
    close() {
      this.$emit('close', this.tours.map((tour) => tour.id));
      this.$emit('input', false);
    },
    tryIt() {
      const route = this.currentSlide.tryRoute;
      this.close();
      if (route && this.$route.path !== route) this.$router.push(route).catch(() => {});
    },
  },
};
</script>

<style scoped>
.feature-tour-media {
  aspect-ratio: 16 / 9;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.feature-tour-media img,
.feature-tour-media video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.feature-tour-text {
  white-space: pre-line;
  line-height: 1.6;
}
.feature-tour-dot {
  width: 8px;
  height: 8px;
  border-radius: 4px;
  margin-inline-end: 6px;
  background: #cbd5e1;
  border: 0;
  padding: 0;
  cursor: pointer;
  transition: width 0.2s ease, background-color 0.2s ease;
}
.feature-tour-dot.active {
  width: 22px;
  background: var(--theme-primary, #018294);
}
</style>
